import { TypeDefinitionAttribute } from '../codegen/graphql';
import { DecoratedAttribute } from './serviceDataValidation';

type TranslateFunction = (key: string) => string;

// ASSET_MAINTENANCE: each select carries an "OTHER" option paired with a free-text
// attribute. The backend always returns the *Other attributes (optional in the TDA)
// and enforces "required when the sibling is OTHER" only at mutation time — showing/
// hiding the paired field and mirroring the conditional required is the frontend's job.
export const MAINTENANCE_OTHER_PAIRS: { [selectIndex: string]: string } = {
    maintenanceType: 'maintenanceTypeOther',
    symptoms: 'symptomsOther',
    issues: 'issuesOther',
    actions: 'actionsOther',
};

const OTHER_VALUE = 'OTHER';

// maintenanceType is a single select (string); symptoms/issues/actions are multi
// selects (string[]) — the trigger is "OTHER" being the value resp. among the values.
export const isOtherSelected = (value: unknown): boolean =>
    Array.isArray(value) ? value.includes(OTHER_VALUE) : value === OTHER_VALUE;

// Picks only the trigger-select values that are actually present in the given form
// values. Steps that don't carry the selects (they only get the seeded *Other keys)
// must not be able to reset a trigger, hence the `in` guard.
export const getMaintenanceOtherTriggerValues = (
    values: Record<string, unknown> | null | undefined
): Record<string, unknown> => {
    const result: Record<string, unknown> = {};
    if (!values) return result;
    for (const selectIndex of Object.keys(MAINTENANCE_OTHER_PAIRS)) {
        if (selectIndex in values) {
            result[selectIndex] = values[selectIndex];
        }
    }
    return result;
};

// Hides each *Other attribute while its paired select isn't set to OTHER and marks it
// required while visible. Filtering the attribute list removes the field, its initial
// value and its validation in one step (the shared TDAForm derives all three from the
// attributes it receives) — same pattern as decorateSpecificationAttributes.
export const decorateMaintenanceOtherAttributes = (
    attributes: TypeDefinitionAttribute[],
    triggerValues: Record<string, unknown>,
    t: TranslateFunction
): DecoratedAttribute[] => {
    const otherToSelect: { [otherIndex: string]: string } = {};
    for (const [selectIndex, otherIndex] of Object.entries(MAINTENANCE_OTHER_PAIRS)) {
        otherToSelect[otherIndex] = selectIndex;
    }

    return attributes
        .filter((attribute) => {
            const selectIndex = otherToSelect[attribute.index];
            if (!selectIndex) return true;
            return isOtherSelected(triggerValues[selectIndex]);
        })
        .map((attribute) => {
            if (!otherToSelect[attribute.index]) return attribute;
            return {
                ...attribute,
                rules: [
                    ...(attribute.rules ?? []).filter((rule) => rule?.name !== 'required'),
                    { name: 'required', message: t('This field is required') },
                ],
            };
        });
};

// Seed values for the *Other keys so they stay in Formik's initial values even while
// their field is filtered out — a changed key set would otherwise reset the whole form
// (TDAForm rebuilds initialValues from the attribute list with enableReinitialize).
// Seeded with the stored attribute value, not bare null: initialFormValues overrides
// the attribute values inside TDAForm, so a null seed would clobber saved text when
// editing an existing event.
export const getMaintenanceOtherSeedValues = (
    attributes: TypeDefinitionAttribute[]
): Record<string, unknown> => {
    const otherIndexes = new Set(Object.values(MAINTENANCE_OTHER_PAIRS));
    const result: Record<string, unknown> = {};
    for (const attribute of attributes) {
        if (otherIndexes.has(attribute.index)) {
            result[attribute.index] = attribute.value ?? null;
        }
    }
    return result;
};

// Submit-time cleanup: TDAForm never auto-clears a select's dependents, so text typed
// while a trigger was active survives in form state after the trigger is deactivated.
// Nulling (rather than omitting) the inactive *Other values also actively clears stale
// stored text when editing an existing event.
export const nullifyInactiveMaintenanceOtherValues = (
    formValues: Record<string, any>
): Record<string, any> => {
    const result = { ...formValues };
    for (const [selectIndex, otherIndex] of Object.entries(MAINTENANCE_OTHER_PAIRS)) {
        if (otherIndex in result && !isOtherSelected(result[selectIndex])) {
            result[otherIndex] = null;
        }
    }
    return result;
};
